import { create } from 'zustand';
import type { ServerEventDTO } from '@clanfire/shared';
import { eventApi } from '@/api/endpoints';

/** Events without an end count as happening for this long after they start. */
export const HAPPENING_FOR_MS = 2 * 60 * 60_000;

interface EventsState {
  /** serverId → eventId → event. */
  byServer: Record<string, Record<string, ServerEventDTO>>;
  loaded: Record<string, boolean>;
  load: (serverId: string) => Promise<void>;
  /** From the API (keeps `interested`) or a broadcast (keeps what we knew about `interested`). */
  upsert: (event: ServerEventDTO, fromBroadcast?: boolean) => void;
  remove: (serverId: string, eventId: string) => void;
}

export const useEvents = create<EventsState>((set, get) => ({
  byServer: {},
  loaded: {},

  load: async (serverId) => {
    const events = await eventApi.list(serverId);
    set((s) => ({ byServer: { ...s.byServer, [serverId]: Object.fromEntries(events.map((e) => [e.id, e])) }, loaded: { ...s.loaded, [serverId]: true } }));
  },

  upsert: (event, fromBroadcast = false) => {
    const current = get().byServer[event.serverId]?.[event.id];
    const next = fromBroadcast ? { ...event, interested: current?.interested ?? false } : event;
    set((s) => ({ byServer: { ...s.byServer, [event.serverId]: { ...s.byServer[event.serverId], [event.id]: next } } }));
  },

  remove: (serverId, eventId) =>
    set((s) => {
      const events = { ...s.byServer[serverId] };
      delete events[eventId];
      return { byServer: { ...s.byServer, [serverId]: events } };
    }),
}));

/** Upcoming and happening events, soonest first (call inside useMemo with the server's record). */
export function sortEvents(events: Record<string, ServerEventDTO> | undefined, now = Date.now()): ServerEventDTO[] {
  return Object.values(events ?? {})
    .filter((e) => (e.endsAt ? Date.parse(e.endsAt) > now : Date.parse(e.startsAt) + HAPPENING_FOR_MS > now))
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}

export function isHappening(event: ServerEventDTO, now = Date.now()): boolean {
  const start = Date.parse(event.startsAt);
  const end = event.endsAt ? Date.parse(event.endsAt) : start + HAPPENING_FOR_MS;
  return start <= now && now < end;
}
